/**
 * Per-tab module-level registry of which follow-up suggestion chips the user
 * has already sent, keyed by the `suggest_followups` tool call that rendered
 * the row (PLT-6117).
 *
 * A chip that has been sent is marked visually but stays clickable, so this is
 * a record rather than a lock — nothing here refuses a click. The mark has to
 * outlive the component: the chat surface is mounted twice (the `/chat/` route
 * and the side panel, see `chat-side-panel.tsx`), and moving between them
 * unmounts one tree and mounts the other against the same lifted runtime. State
 * held in the chip would reset on that move while the message it belongs to
 * stayed on screen, which is the defect this registry exists to remove.
 *
 * `toolCallId` is the key because it is the only per-message identity a tool UI
 * receives, and it is stable for the life of the runtime. The prompt is the
 * second key rather than the chip's index, so a row's marks survive a re-render
 * that reorders or re-slices `args.suggestions` mid-stream.
 *
 * Scope is the tab. For a row the model emits, a reload loses the *row*, not
 * just the marks: `suggest_followups` is deliberately excluded from
 * `REPLAYABLE_UI_ONLY_TOOLS` (`chat-transcript/parser.ts`, PLT-5429) — replaying
 * a chip row from history would re-offer a stale action rather than restate a
 * summary — so a mark stored with the message would have nothing to attach to.
 * The one row that does come back on reopen is the brief chips of the server's
 * "assessment is ready" message (`injectAssessmentReadyMessage`, PLT-7428),
 * which are saved with it because they are the message. Its marks still reset
 * on a reload, so a sent brief shows unsent again. Persisting marks would only
 * pay for that one row.
 *
 * Same pattern (and rationale) as `tool-progress-registry.ts` and
 * `visibility-registry.ts`: a module-level Map plus a listener set, consumed
 * from React through `useSyncExternalStore`.
 */

const usedPromptsByToolCall = new Map<string, Set<string>>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

/** Record that the user sent this chip's prompt from this row. Idempotent. */
export function markFollowupUsed(toolCallId: string, prompt: string): void {
  if (!toolCallId) return;
  const used = usedPromptsByToolCall.get(toolCallId);
  if (used) {
    if (used.has(prompt)) return; // no-op: avoid a needless re-render
    used.add(prompt);
  } else {
    usedPromptsByToolCall.set(toolCallId, new Set([prompt]));
  }
  notify();
}

export function isFollowupUsed(toolCallId: string, prompt: string): boolean {
  return usedPromptsByToolCall.get(toolCallId)?.has(prompt) ?? false;
}

export function subscribeFollowupsUsed(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only helper. Production code should not rely on the registry being empty. */
export function clearAllFollowupsUsed(): void {
  usedPromptsByToolCall.clear();
  notify();
}
